import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import api from "../../utils/api";

export type TsUploadAccess = {
  allowed: boolean;
  isSuperAdmin: boolean;
  atReadyToUpload: boolean;
  allocationStatus: string | null;
  allocationCaseId: string | null;
  bypassExpiresAt: string | null;
  bypassGrantedBy: string | null;
  reason:
    | "super_admin"
    | "ready_to_upload"
    | "case_completed"
    | "on_hold"
    | "bypass"
    | "not_ready";
  message: string;
};

type TsUploadAccessState = {
  caseId: string | null;
  access: TsUploadAccess | null;
  loading: boolean;
  granting: boolean;
  error: string | null;
};

const initialState: TsUploadAccessState = {
  caseId: null,
  access: null,
  loading: false,
  granting: false,
  error: null,
};

export const fetchTsUploadAccess = createAsyncThunk(
  "tsUploadAccess/fetch",
  async (caseId: string, { rejectWithValue }) => {
    try {
      const res = await api.get(`/auth/admin/case/${caseId}/ts-upload-access`);
      return { caseId, access: res.data.data as TsUploadAccess };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to load upload access",
        );
      }
      return rejectWithValue("Failed to load upload access");
    }
  },
);

export const grantCrmTsUploadBypass = createAsyncThunk(
  "tsUploadAccess/grant",
  async (caseId: string, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/auth/admin/case/${caseId}/crm-upload-bypass`,
      );
      return { caseId, access: res.data.data as TsUploadAccess };
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        return rejectWithValue(
          err.response?.data?.message || "Failed to grant Operations upload access",
        );
      }
      return rejectWithValue("Failed to grant Operations upload access");
    }
  },
);

const tsUploadAccessSlice = createSlice({
  name: "tsUploadAccess",
  initialState,
  reducers: {
    clearTsUploadAccess: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTsUploadAccess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTsUploadAccess.fulfilled, (state, action) => {
        state.loading = false;
        state.caseId = action.payload.caseId;
        state.access = action.payload.access;
      })
      .addCase(fetchTsUploadAccess.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || "Failed to load upload access";
      })
      .addCase(grantCrmTsUploadBypass.pending, (state) => {
        state.granting = true;
        state.error = null;
      })
      .addCase(grantCrmTsUploadBypass.fulfilled, (state, action) => {
        state.granting = false;
        state.caseId = action.payload.caseId;
        state.access = {
          ...action.payload.access,
          allowed: true,
          reason: action.payload.access.reason || "bypass",
        };
      })
      .addCase(grantCrmTsUploadBypass.rejected, (state, action) => {
        state.granting = false;
        state.error =
          (action.payload as string) || "Failed to grant Operations upload access";
      });
  },
});

export const { clearTsUploadAccess } = tsUploadAccessSlice.actions;
export default tsUploadAccessSlice.reducer;
